/**
 * Helper components for map control and interaction
 */

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { LatLngBounds } from 'leaflet';
import { MAP_BOUNDS_PADDING, MAP_MAX_ZOOM } from './map-constants';
import { COLOR_SCHEMES } from './MapStyleControl';

interface MapBoundsUpdaterProps {
    bounds: LatLngBounds | null;
    isAnimating?: boolean;
}

/**
 * Component to handle map bounds updates - disabled during transitions
 */
export function MapBoundsUpdater({ bounds, isAnimating }: MapBoundsUpdaterProps) {
    const map = useMap();

    useEffect(() => {
        if (isAnimating) return;
        if (bounds && bounds.isValid()) {
            map.fitBounds(bounds, { padding: MAP_BOUNDS_PADDING, maxZoom: MAP_MAX_ZOOM });
        }
    }, [bounds, map, isAnimating]);

    return null;
}

interface MapViewSetterProps {
    center: { lat: number; lng: number } | null;
    zoom?: number;
}

/**
 * Explicitly sets the map view - used at the start of transitions
 */
export function MapViewSetter({ center, zoom = 15 }: MapViewSetterProps) {
    const map = useMap();

    useEffect(() => {
        if (center) {
            map.setView([center.lat, center.lng], zoom, { animate: false });
        }
    }, [map, center, zoom]);

    return null;
}

interface MapColorFilterProps {
    colorScheme: string;
}

/**
 * Component to apply color scheme filter to map tiles
 */
export function MapColorFilter({ colorScheme }: MapColorFilterProps) {
    const map = useMap();

    useEffect(() => {
        const scheme = COLOR_SCHEMES.find(s => s.id === colorScheme);
        if (scheme) {
            const container = map.getContainer();
            const tilePane = container.querySelector('.leaflet-tile-pane') as HTMLElement;
            if (tilePane) {
                tilePane.style.filter = scheme.filter;
            }
        }
    }, [colorScheme, map]);

    return null;
}

interface MapClickHandlerProps {
    onMapClick: () => void;
}


/**
 * Component to handle map clicks (e.g., close sidebar on mobile)
 */
export function MapClickHandler({ onMapClick }: MapClickHandlerProps) {
    const map = useMap();

    useEffect(() => {
        const handleClick = () => {
            onMapClick();
        };

        map.on('click', handleClick);

        return () => {
            map.off('click', handleClick);
        };
    }, [map, onMapClick]);

    return null;
}

/**
 * Component to stop map animations and events before the map is removed.
 * This prevents "Cannot read properties of undefined (reading '_leaflet_pos')" errors
 * during rapid navigation or unmounting during zoom transitions.
 */
export function MapAnimationStopper() {
    const map = useMap();

    useEffect(() => {
        return () => {
            try {
                // Remove all event listeners first to prevent any further callbacks
                map.off();
                
                // Only attempt to stop if the map instance and its container/panes are still valid
                // The error "reading '_leaflet_pos'" often happens inside map.stop() 
                // if the DOM has already been significantly altered or removed.
                const container = map.getContainer();
                const pane = map.getPane('mapPane');
                
                if (container && pane && document.body.contains(container)) {
                    map.stop();
                }
            } catch (e) {
                // Silently ignore — the map DOM was already torn down or is in an unstable state
                console.debug('MapAnimationStopper: Suppressed error during cleanup', e);
            }
        };
    }, [map]);

    return null;
}

